import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "../../../app/store";
import { InitiateJoinResponse } from "../../../api/initiateJoin/types";
import {
  InitiateSelfNominationRequest,
  InitiateSelfNominationResponse,
} from "../../../api/initiateSelfNomination/types";
import { initiateSelfNomination } from "../../../api/initiateSelfNomination/initiateSelfNominationApi";
import { initiateJoinByContactId } from "../../../api/initiateJoinByContactId/initiateJoinByContactIdApi";

interface RegistrationState {
  inviteCode: string | null;
  loading: boolean;
  successMessage: string | null;
  error: string | null;
  submissionStatus: string | null;
  contactId: string | null;
  joinSessionUuid: string | null;
}

const initialState: RegistrationState = {
  inviteCode: null,
  loading: false,
  successMessage: null,
  error: null,
  submissionStatus: null,
  contactId: null,
  joinSessionUuid: null,
};

const getStartedSlice = createSlice({
  name: "membership",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setInviteCode: (state, action: PayloadAction<string | null>) => {
      state.inviteCode = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSubmissionStatus: (state, action: PayloadAction<string | null>) => {
      state.submissionStatus = action.payload;
    },
    setContactId: (state, action: PayloadAction<string | null>) => {
      state.contactId = action.payload;
    },
    setJoinSessionUuid: (state, action: PayloadAction<string | null>) => {
      state.joinSessionUuid = action.payload;
    },
  },
});

export const {
  setLoading,
  setInviteCode,
  setSuccessMessage,
  setError,
  setSubmissionStatus,
  setContactId,
  setJoinSessionUuid,
} = getStartedSlice.actions;

export const initiateSelfNominationAction =
  (
    requestData: InitiateSelfNominationRequest,
    callback?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: InitiateSelfNominationResponse =
        await initiateSelfNomination(requestData);
      dispatch(setJoinSessionUuid(response.data.uuid));
      dispatch(setSuccessMessage(response.data.message));

      localStorage.setItem("joinSessionUuid", response.data.uuid);

      if (callback) callback();
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to initiate the self-nomination process.",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const initiateJoinByContactIdAction =
  (hubspotContactId: string, callback?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: InitiateJoinResponse =
        await initiateJoinByContactId(hubspotContactId);

      dispatch(setJoinSessionUuid(response.data.uuid));
      dispatch(setSuccessMessage(response.data.message));

      localStorage.setItem("joinSessionUuid", response.data.uuid);

      if (callback) callback();
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to initiate join process.",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default getStartedSlice.reducer;
